import * as THREE from 'three';

/**
 * Procedural materials for the sealed door. The stone is drawn as a height field first (grain, undulation, pits, fissures, carved
 * marks) and its colour and normal map are both derived from that, so light from the gem catches the relief rather than a painted
 * picture of it. Everything is seeded and drawn at load; no bitmap textures ship.
 */

const SIZE = 1024;
/** World units covered by one repeat of the stone texture. */
export const STONE_TILE = 4;

export interface StoneTextures { map: THREE.CanvasTexture; normalMap: THREE.CanvasTexture }

export function seeded(seed: number) {
  return () => {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Smoothly interpolated value noise that wraps at 0 and 1, so the texture tiles. */
export function periodicNoise(random: () => number, cells: number) {
  const grid = new Float32Array(cells * cells).map(() => random());
  const smooth = (t: number) => t * t * (3 - 2 * t);
  return (u: number, v: number) => {
    const x = (((u % 1) + 1) % 1) * cells, y = (((v % 1) + 1) % 1) * cells;
    const x0 = Math.floor(x), y0 = Math.floor(y), fx = smooth(x - x0), fy = smooth(y - y0);
    const x1 = (x0 + 1) % cells, y1 = (y0 + 1) % cells;
    const top = grid[y0 * cells + x0] * (1 - fx) + grid[y0 * cells + x1] * fx, bottom = grid[y1 * cells + x0] * (1 - fx) + grid[y1 * cells + x1] * fx;
    return top * (1 - fy) + bottom * fy;
  };
}

/** The alpha channel of a canvas as 0..1 values, softened by `passes` box blurs of `radius` pixels (wrapping at the edges). */
export function softAlpha(canvas: HTMLCanvasElement, radius: number, passes: number): Float32Array {
  const size = canvas.width, data = canvas.getContext('2d')!.getImageData(0, 0, size, size).data;
  let from = new Float32Array(size * size), to = new Float32Array(size * size);
  for (let i = 0; i < from.length; i++) from[i] = data[i * 4 + 3] / 255;
  const width = radius * 2 + 1;
  for (let pass = 0; pass < passes; pass++) {
    for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) { let sum = 0; for (let k = -radius; k <= radius; k++) sum += from[y * size + ((x + k + size) % size)]; to[y * size + x] = sum / width; }
    [from, to] = [to, from];
    for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) { let sum = 0; for (let k = -radius; k <= radius; k++) sum += from[((y + k + size) % size) * size + x]; to[y * size + x] = sum / width; }
    [from, to] = [to, from];
  }
  return from;
}

/** A normal map from a height field, wrapping at the edges. `strength` scales the slope. */
export function normalCanvas(height: Float32Array, size: number, strength: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas'); canvas.width = canvas.height = size;
  const context = canvas.getContext('2d')!, image = context.createImageData(size, size), data = image.data;
  const at = (x: number, y: number) => height[((y + size) % size) * size + ((x + size) % size)];
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    // Canvas rows run down while v runs up, so the row slope is the v slope with its sign flipped back.
    const nx = -(at(x + 1, y) - at(x - 1, y)) * strength, ny = (at(x, y + 1) - at(x, y - 1)) * strength, inv = 1 / Math.hypot(nx, ny, 1), i = (y * size + x) * 4;
    data[i] = (nx * inv * .5 + .5) * 255; data[i + 1] = (ny * inv * .5 + .5) * 255; data[i + 2] = (inv * .5 + .5) * 255; data[i + 3] = 255;
  }
  context.putImageData(image, 0, 0);
  return canvas;
}

function texture(canvas: HTMLCanvasElement, anisotropy: number, color: boolean): THREE.CanvasTexture {
  const result = new THREE.CanvasTexture(canvas);
  result.wrapS = result.wrapT = THREE.RepeatWrapping; result.anisotropy = anisotropy;
  if (color) result.colorSpace = THREE.SRGBColorSpace;
  return result;
}

/**
 * One face of dressed stone, tileable in both directions. Each brick samples its own window of it, so the bricks are all different:
 * grain, undulation and pitting; a few fissures carved into the surface with soft, shaded walls rather than painted lines; stains;
 * and a few carved marks. The colour is a neutral grey that each brick tints for itself.
 */
export function stoneTextures(seed: number, anisotropy: number): StoneTextures {
  const random = seeded(seed), between = (a: number, b: number) => a + random() * (b - a), chance = (p: number) => random() < p;
  const coarse = periodicNoise(random, 5), mid = periodicNoise(random, 22), fine = periodicNoise(random, 170), grit = periodicNoise(random, 400);
  const height = new Float32Array(SIZE * SIZE), tone = new Float32Array(SIZE * SIZE);
  for (let y = 0; y < SIZE; y++) for (let x = 0; x < SIZE; x++) {
    const u = x / SIZE, v = y / SIZE, g = fine(u, v), m = mid(u, v), c = coarse(u, v), k = grit(u, v), i = y * SIZE + x;
    height[i] = .5 + (m - .5) * .36 + (c - .5) * .24 + (g - .5) * .22 + (k - .5) * .1;
    tone[i] = .86 + .5 * (g - .5) + .45 * (c - .5) + .3 * (m - .5) + .25 * (k - .5);
  }

  const canvasOf = () => { const canvas = document.createElement('canvas'); canvas.width = canvas.height = SIZE; return canvas; };
  const fissures = canvasOf(), pits = canvasOf(), stains = canvasOf(), carvings = canvasOf();
  // These are read back (`softAlpha`, the stains): canvases kept in memory answer without stalling for the GPU.
  const read = (canvas: HTMLCanvasElement) => canvas.getContext('2d', { willReadFrequently: true })!;
  const fissure = read(fissures), pit = read(pits), stain = read(stains), carve = read(carvings);

  // Fissures: a wandering main line that thins as it goes, with short branches. It is softened below, so the walls slope instead of showing a stroke.
  fissure.strokeStyle = '#000'; fissure.lineCap = 'round';
  const crack = (x: number, y: number, heading: number, length: number, width: number, depth: number) => {
    for (let travelled = 0; travelled < length; ) {
      const step = between(10, 22), nx = x + Math.cos(heading) * step, ny = y + Math.sin(heading) * step;
      fissure.globalAlpha = Math.max(.25, 1 - depth * .3); fissure.lineWidth = Math.max(2.5, width * (1 - travelled / length * .7));
      fissure.beginPath(); fissure.moveTo(x, y); fissure.lineTo(nx, ny); fissure.stroke();
      if (depth < 2 && chance(.16)) crack(nx, ny, heading + (chance(.5) ? 1 : -1) * between(.5, 1.1), length * .35, width * .6, depth + 1);
      x = nx; y = ny; heading += between(-.5, .5); travelled += step;
    }
  };
  for (let n = 0; n < 6; n++) crack(between(120, SIZE - 120), between(120, SIZE - 120), between(0, Math.PI * 2), between(200, 440), between(7, 11), 0);

  // Pitting: small dents, and a scatter of fine ones.
  for (let n = 0; n < 1400; n++) {
    const x = between(0, SIZE), y = between(0, SIZE), r = chance(.12) ? between(4, 9) : between(1.2, 3.4), gradient = pit.createRadialGradient(x, y, 0, x, y, r);
    gradient.addColorStop(0, 'rgba(0,0,0,.9)'); gradient.addColorStop(1, 'rgba(0,0,0,0)'); pit.fillStyle = gradient; pit.fillRect(x - r, y - r, r * 2, r * 2);
  }

  // Weathering: soft dark and dusty patches, drawn nine times over so they wrap.
  for (let n = 0; n < 16; n++) {
    const x = between(0, SIZE), y = between(0, SIZE), rx = between(50, 190), ry = between(20, 70), dark = chance(.65);
    for (const dx of [0, -SIZE, SIZE]) for (const dy of [0, -SIZE, SIZE]) {
      const gradient = stain.createRadialGradient(x + dx, y + dy, 0, x + dx, y + dy, rx);
      gradient.addColorStop(0, dark ? 'rgba(0,0,0,.34)' : 'rgba(255,245,225,.2)'); gradient.addColorStop(1, 'rgba(0,0,0,0)');
      stain.save(); stain.translate(x + dx, y + dy); stain.scale(1, ry / rx); stain.translate(-(x + dx), -(y + dy)); stain.fillStyle = gradient; stain.fillRect(x + dx - rx, y + dy - rx, rx * 2, rx * 2); stain.restore();
    }
  }

  // A few carved marks, a few strokes in a rune's proportions, cut shallower than a fissure.
  carve.strokeStyle = '#000'; carve.lineCap = 'round'; carve.lineWidth = 6;
  for (let n = 0; n < 6; n++) {
    const gx = between(100, SIZE - 160), gy = between(100, SIZE - 160); carve.beginPath(); carve.moveTo(gx - 12, gy - 22); carve.lineTo(gx - 12, gy + 60);
    for (let k = 0, count = 3 + Math.floor(random() * 3); k < count; k++) { carve.moveTo(gx + Math.floor(random() * 3) * 22, gy + Math.floor(random() * 3) * 22); carve.lineTo(gx + Math.floor(random() * 3) * 22, gy + Math.floor(random() * 3) * 22); }
    carve.stroke();
  }

  const cut = softAlpha(fissures, 4, 2), dents = softAlpha(pits, 1, 1), carved = softAlpha(carvings, 2, 1);
  const stainData = stains.getContext('2d')!.getImageData(0, 0, SIZE, SIZE).data;
  const albedo = canvasOf(), albedoContext = albedo.getContext('2d')!, image = albedoContext.createImageData(SIZE, SIZE);
  for (let i = 0; i < SIZE * SIZE; i++) {
    const crackDepth = Math.min(1, cut[i] * 1.8), stainAlpha = stainData[i * 4 + 3] / 255, light = stainData[i * 4] > 128;
    height[i] -= crackDepth * .5 + dents[i] * .4 + carved[i] * .4;
    // Fissures and pits are darker for being deep and shaded, not black: the normal map and the lights do the rest.
    const k = tone[i] * (1 - crackDepth * .22 - dents[i] * .4 - carved[i] * .4) * (light ? 1 + stainAlpha * .5 : 1 - stainAlpha * .5);
    image.data[i * 4] = 168 * k; image.data[i * 4 + 1] = 162 * k; image.data[i * 4 + 2] = 152 * k; image.data[i * 4 + 3] = 255;
  }
  albedoContext.putImageData(image, 0, 0);
  return { map: texture(albedo, anisotropy, true), normalMap: texture(normalCanvas(height, SIZE, 7), anisotropy, false) };
}

/** A fine hammered-metal bump for the bands, bolts and bronze work, and a gritty one for the mortar. */
export function hammeredNormal(seed: number, anisotropy: number): THREE.CanvasTexture {
  const size = 256, random = seeded(seed), broad = periodicNoise(random, 9), fine = periodicNoise(random, 64);
  const height = new Float32Array(size * size);
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) height[y * size + x] = broad(x / size, y / size) * .6 + fine(x / size, y / size) * .4;
  return texture(normalCanvas(height, size, 2.2), anisotropy, false);
}

/** A soft round glow for additive sprites. */
export function glowTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas'); canvas.width = canvas.height = 128;
  const context = canvas.getContext('2d')!, gradient = context.createRadialGradient(64, 64, 0, 64, 64, 64);
  gradient.addColorStop(0, 'rgba(255,255,255,1)'); gradient.addColorStop(.18, 'rgba(255,255,255,.55)'); gradient.addColorStop(.5, 'rgba(255,255,255,.12)'); gradient.addColorStop(1, 'rgba(255,255,255,0)');
  context.fillStyle = gradient; context.fillRect(0, 0, 128, 128);
  return new THREE.CanvasTexture(canvas);
}
